/**
 * DATA SOURCE FLAGS
 * Controls whether a specific domain uses live API data or dev-bypass mock data.
 * Used during the staged cutover to live data.
 */

import { isDevAuthBypassEnabled } from './devMode';

const truthyValues = new Set(['1', 'true', 'yes', 'on']);

const readLiveFlag = (envValue: string | undefined) => truthyValues.has((envValue ?? '').toLowerCase());

// CONSTRAINT: If auth bypass is OFF, all domains MUST be live by default to ensure production safety.
// If auth bypass is ON, each domain opt-in is controlled by its own env flag.
const resolveLiveFlag = (envValue: string | undefined) => (!isDevAuthBypassEnabled ? true : readLiveFlag(envValue));

export const dataSourceFlags = {
  get useLiveGroups() {
    return resolveLiveFlag(import.meta.env.VITE_LIVE_GROUPS);
  },
  get useLiveUsers() {
    return resolveLiveFlag(import.meta.env.VITE_LIVE_USERS);
  },
  get useLiveContent() {
    return resolveLiveFlag(import.meta.env.VITE_LIVE_CONTENT);
  },
  get useLiveSessions() {
    return resolveLiveFlag(import.meta.env.VITE_LIVE_SESSIONS);
  },
  get useLiveTasks() {
    return resolveLiveFlag(import.meta.env.VITE_LIVE_TASKS);
  },
  get useLiveGoals() {
    // WHY: Goals is still under active local development and the backend contract is not
    // reliably available yet. In local dev, default to the local goals path unless the
    // operator explicitly opts into the live endpoint.
    if (import.meta.env.DEV) {
      return readLiveFlag(import.meta.env.VITE_LIVE_GOALS);
    }

    return resolveLiveFlag(import.meta.env.VITE_LIVE_GOALS);
  },
  get useLiveSurveys() {
    return resolveLiveFlag(import.meta.env.VITE_LIVE_SURVEYS);
  }
};
